"use client";

import { Input } from "@/components/rac/input";
import { type Country } from "@/info/countries";
import { isValidPhoneNumber } from "@/info/login";
import { type ChangeEvent, type KeyboardEvent } from "react";
import { Label } from "react-aria-components";

interface PhoneInputProps {
	phoneNumber: string;
	onPhoneNumberChange: (phone: string) => void;
	selectedCountry: Country;
	onEnterPress?: () => void;
	isDisabled?: boolean;
	placeholder?: string;
	hideError?: boolean;
	phoneVerified?: boolean;
	label?: string;
}

export function PhoneInput({
	phoneNumber,
	onPhoneNumberChange,
	selectedCountry,
	onEnterPress,
	isDisabled = false,
	placeholder = "Enter your number",
	hideError = false,
	phoneVerified = false,
	label = "Phone Number",
}: PhoneInputProps) {
	const handlePhoneChange = (event: ChangeEvent<HTMLInputElement>) => {
		// Remove any non-digit characters except +
		const cleaned = event.target.value.replace(/[^\d]/g, "");
		onPhoneNumberChange(cleaned);
	};

	const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
		if (event.key === "Enter" && onEnterPress && isValid && phoneNumber.length > 0) {
			event.preventDefault();
			onEnterPress();
		}
	};

	const isValid = phoneNumber.length === 0 || isValidPhoneNumber(phoneNumber, selectedCountry.code);

	return (
		<div className="space-y-2 mb-4">
			<Label className="text-sm font-medium">{label}</Label>
			<div className="relative">
				<Input
					type="tel"
					value={phoneNumber}
					onChange={handlePhoneChange}
					onKeyDown={handleKeyDown}
					placeholder={placeholder}
					className={`w-full h-12 px-3 py-3 text-sm border rounded-lg transition-all duration-200 touch-manipulation ${
						isValid ? "border-gray-300" : "border-red-300"
					} ${isDisabled ? "opacity-50 cursor-not-allowed" : ""}`}
					disabled={isDisabled}
					autoComplete="tel"
					inputMode="numeric"
					maxLength={10}
				/>
				{phoneVerified && (
					<span className="absolute right-3 top-1/2 -translate-y-1/2 bg-blue-600 text-white text-xs px-2 py-1 rounded">
						verified
					</span>
				)}
			</div>
			{!hideError && !isValid && phoneNumber.length > 0 && (
				<p className="text-xs text-red-600 mt-1">Please enter a valid phone number</p>
			)}
		</div>
	);
}
